import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { SyncBadge } from "@/components/common/SyncBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { dt, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export default function Reconciliation() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const queryClient = useQueryClient();
  const [interval, setIntervalValue] = useState(300);

  const { data, isLoading } = useQuery({
    queryKey: ["reconciliation"],
    queryFn: () => get("/provider-center/reconciliation"),
    refetchInterval: 30000,
  });

  const settings = useQuery({
    queryKey: ["reconciliation-settings"],
    queryFn: () => get("/provider-center/reconciliation/settings"),
  });

  const saveSettings = useMutation({
    mutationFn: (payload) => api.put("/provider-center/reconciliation/settings", payload),
    onSuccess: () => {
      toast.success("Reconciliation schedule updated");
      queryClient.invalidateQueries({ queryKey: ["reconciliation-settings"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const run = useMutation({
    mutationFn: () => api.post("/provider-center/reconciliation/run"),
    onSuccess: ({ data: r }) => {
      toast.success(`Reconciled ${r.agents_checked} agents and ${r.numbers?.checked ?? 0} numbers`);
      queryClient.invalidateQueries();
    },
    onError: (e) => toast.error(apiError(e)),
  });

  useEffect(() => {
    if (settings.data?.interval_seconds) setIntervalValue(settings.data.interval_seconds);
  }, [settings.data?.interval_seconds]);

  const resolve = useMutation({
    mutationFn: ({ row, action }) =>
      api.post(`/provider-center/reconciliation/${row.resource}/${row.id}/${action}`),
    onSuccess: () => {
      toast.success("Drift resolution applied");
      queryClient.invalidateQueries();
    },
    onError: (e) => toast.error(
      e.response?.data?.detail?.reason || e.response?.data?.detail?.message || apiError(e)),
  });

  return (
    <>
      <PageHeader
        testId="reconciliation-page"
        title="Reconciliation"
        subtitle={`Desired state versus provider state · last run ${data?.last_run_at ? dt(data.last_run_at) : "never"}`}
        actions={isAdmin ? (
          <Button className="bg-blue-600 hover:bg-blue-700" disabled={run.isPending} onClick={() => run.mutate()} data-testid="reconcile-run">
            {run.isPending ? "Reconciling…" : "Run reconciliation"}
          </Button>
        ) : null}
      />

      <Section testId="reconciliation-schedule" title="Background reconciliation">
        <div className="flex flex-wrap items-end gap-4 p-4" data-testid="reconciliation-settings">
          <div className="space-y-1.5">
            <Label>Interval (seconds)</Label>
            <Input
              data-testid="reconcile-interval-input"
              type="number"
              min="30"
              className="w-40"
              value={interval}
              onChange={(e) => setIntervalValue(e.target.value)}
              disabled={!isAdmin}
            />
          </div>
          <div className="flex items-center gap-2 pb-1.5">
            <Switch
              data-testid="reconcile-enabled-switch"
              checked={settings.data ? settings.data.enabled !== false : false}
              disabled={!isAdmin}
              onCheckedChange={(v) => saveSettings.mutate({ enabled: v })}
            />
            <span className="text-sm text-slate-700">Job enabled</span>
          </div>
          {isAdmin ? (
            <Button
              variant="outline"
              data-testid="reconcile-settings-save"
              disabled={saveSettings.isPending}
              onClick={() => saveSettings.mutate({ interval_seconds: Number(interval) })}
            >
              Save interval
            </Button>
          ) : null}
          <p className="text-xs text-slate-500" data-testid="reconcile-last-result">
            Last run {data?.last_run_at ? dt(data.last_run_at) : "never"} ·
            {" "}{settings.data?.last_result?.drift_total ?? 0} drifting resources ·
            {" "}{settings.data?.last_result?.errors?.length ?? 0} errors · drift is never
            resolved automatically.
          </p>
        </div>
      </Section>

      <Section testId="reconciliation-section" title={`${(data?.rows || []).length} provider-backed resources`}>
        <DataTable
          testId="reconciliation-table"
          rowTestId="reconcile-row"
          loading={isLoading}
          columns={[
            { key: "resource", label: "Resource", render: (r) => title(r.resource) },
            { key: "name", label: "Name", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
            { key: "provider", label: "Provider", render: (r) => title(r.provider || "—") },
            { key: "provider_resource_id", label: "Provider ID", render: (r) => <span className="font-mono text-[11px]">{r.provider_resource_id || "—"}</span> },
            { key: "local_status", label: "Local", render: (r) => title(r.local_status || "—") },
            { key: "remote_status", label: "Remote", render: (r) => title(r.remote_status || "—") },
            { key: "sync_status", label: "Sync", render: (r) => <SyncBadge status={r.sync_status} testId={`sync-${r.id}`} /> },
            { key: "last_checked", label: "Last checked", render: (r) => dt(r.last_checked) },
            { key: "last_synced", label: "Last synced", render: (r) => dt(r.last_synced) },
            { key: "error", label: "Error", render: (r) => <span className="line-clamp-1 block max-w-[16rem] text-xs text-rose-600">{r.error || "—"}</span> },
            { key: "recommended_action", label: "Recommended action", render: (r) => <span className="text-xs text-slate-600">{r.recommended_action}</span> },
            {
              key: "actions",
              label: "",
              render: (r) => (!["agent", "phone_number"].includes(r.resource) ? (
                <span className="text-xs text-slate-400">Resolve on the underlying resource</span>
              ) : isAdmin ? (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => resolve.mutate({ row: r, action: "retry" })} data-testid={`reconcile-retry-${r.id}`}>Retry</Button>
                  <Button size="sm" variant="ghost" onClick={() => resolve.mutate({ row: r, action: "reapply" })} data-testid={`reconcile-reapply-${r.id}`}>Reapply local</Button>
                  <Button size="sm" variant="ghost" onClick={() => resolve.mutate({ row: r, action: "accept_remote" })} data-testid={`reconcile-accept-${r.id}`}>Accept remote</Button>
                </div>
              ) : <span className="text-xs text-slate-400">Super Admin only</span>),
            },
          ]}
          rows={data?.rows || []}
          empty={{ title: "No provider-backed resources yet", description: "Connect a provider and synchronise an agent or phone number." }}
        />
      </Section>
    </>
  );
}
